import { makeAutoObservable, runInAction } from 'mobx'
import { CostingService } from '~/shared/api/services/costing'
import {
  CostingFilters,
  CostingMaterialResponseDTO,
  CostingOperationResponseDTO,
  CostingProductResponseDTO,
} from '~/shared/api/services/costing/types'
import { WarehousesService } from '~/shared/api/services/warehouses'
import { Suppliers } from '~/shared/api/services/warehouses/types'

const INITIAL_FILTERS: CostingFilters = {
  projects: [],
  productGroups: [],
  productSubgroups: [],
  workshops: [],
  materialGroups: [],
  operationGroups: [],
  unitCodes: [],
}

type CostingSearchKey = 'products' | 'materials' | 'operations'
type CostingMaterialFilterKey = 'supplierName' | 'materialGroup'

export class CostingStore {
  products: CostingProductResponseDTO[] = []

  materials: CostingMaterialResponseDTO[] = []

  operations: CostingOperationResponseDTO[] = []

  filters: CostingFilters = INITIAL_FILTERS

  suppliers: Suppliers[] = []

  loaders = {
    products: false,
    materials: false,
    operations: false,
    lookups: false,
  }

  search = {
    products: '',
    materials: '',
    operations: '',
  }

  materialFilters = {
    supplierName: '',
    materialGroup: '',
  }

  constructor() {
    makeAutoObservable(this)
  }

  setSearch = (key: CostingSearchKey, value: string) => {
    this.search[key] = value
  }

  setMaterialFilter = (key: CostingMaterialFilterKey, value: string) => {
    this.materialFilters[key] = value
  }

  get filteredMaterials() {
    const searchValue = this.search.materials.trim().toLowerCase()

    return this.materials.filter((material) => {
      const matchesSearch = searchValue
        ? [material.name, material.code]
            .filter(Boolean)
            .some((value) => value?.toLowerCase().includes(searchValue))
        : true
      const matchesSupplier = this.materialFilters.supplierName
        ? material.supplierName === this.materialFilters.supplierName
        : true
      const matchesGroup = this.materialFilters.materialGroup
        ? material.materialGroup === this.materialFilters.materialGroup
        : true

      return matchesSearch && matchesSupplier && matchesGroup
    })
  }

  loadProducts = async () => {
    this.loaders.products = true

    try {
      const response = await CostingService.getProducts({
        q: this.search.products.trim() || null,
        limit: 100,
        offset: 0,
      })

      runInAction(() => {
        this.products = response.data
      })
    } finally {
      runInAction(() => {
        this.loaders.products = false
      })
    }
  }

  loadMaterials = async () => {
    this.loaders.materials = true

    try {
      const response = await CostingService.getMaterials({
        q: this.search.materials.trim() || null,
        limit: 100,
        offset: 0,
      })

      runInAction(() => {
        this.materials = response.data
      })
    } finally {
      runInAction(() => {
        this.loaders.materials = false
      })
    }
  }

  loadOperations = async () => {
    this.loaders.operations = true

    try {
      const response = await CostingService.getOperations({
        q: this.search.operations.trim() || undefined,
        limit: 100,
        offset: 0,
      })

      runInAction(() => {
        this.operations = response.data
      })
    } finally {
      runInAction(() => {
        this.loaders.operations = false
      })
    }
  }

  loadLookups = async () => {
    this.loaders.lookups = true

    try {
      const [filtersResponse, suppliers] = await Promise.all([
        CostingService.getFilters(),
        WarehousesService.getSuppliers().then((response) => response.data),
      ])

      runInAction(() => {
        this.filters = filtersResponse.data
        this.suppliers = suppliers
      })
    } finally {
      runInAction(() => {
        this.loaders.lookups = false
      })
    }
  }

  loadReferenceCatalogs = async () => {
    await Promise.all([
      this.loadLookups(),
      this.loadMaterials(),
      this.loadOperations(),
    ])
  }

  getMaterialName = (id: string | null) => {
    if (!id) {
      return 'Материал не выбран'
    }

    return this.materials.find((item) => item.id === id)?.name || id
  }

  getOperationName = (id: string | null) => {
    if (!id) {
      return 'Операция не выбрана'
    }

    return this.operations.find((item) => item.id === id)?.name || id
  }
}
